import { useEffect, useRef } from 'react'
import { fetchWithAuth, getStoredUser, supabase } from '@/lib/api/client'
import { loadWallet } from '@/lib/wallet-utils'
import { payForListing } from '@/lib/x402/client'
import type { RealtimeChannel } from '@supabase/supabase-js'

const API_URL = import.meta.env.VITE_API_URL!

interface PurchaseRequest {
  id: string
  userId: string
  listingId: string
  createdAt: number
  status: string
}

/**
 * Background component that listens for purchase requests via Supabase Realtime
 */
export function MarketplacePurchaseExecutor() {
  const processingRef = useRef<Set<string>>(new Set())
  const channelRef = useRef<RealtimeChannel | null>(null)

  useEffect(() => {
    const initPurchaseListener = async () => {
      // Get current user from Supabase session
      const user = await getStoredUser()
      if (!user?.id) {
        return
      }

      // Get access token from Supabase session
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (!session?.access_token) {
        return
      }
      const accessToken = session.access_token

      // Function to handle incoming purchase requests
      const handlePurchaseRequest = async (request: PurchaseRequest) => {
        // Skip if already processing this request
        if (processingRef.current.has(request.id)) {
          return
        }

        const {
          data: { session },
        } = await supabase.auth.getSession()
        if (!session?.access_token) {
          console.error('[Marketplace] No session available for purchase')
          return
        }
        const accessToken = session.access_token

        // Mark as processing
        processingRef.current.add(request.id)

        // Execute the purchase (don't await - process in parallel)
        executePurchase(request, accessToken).finally(() => {
          // Remove from processing set when done
          processingRef.current.delete(request.id)
        })
      }

      // Subscribe to purchase requests via Supabase Realtime
      const channel = supabase
        .channel('purchase-requests')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'marketplace_purchase_requests',
            filter: `buyer_user_id=eq.${user.id}`,
          },
          async (payload) => {
            const request: PurchaseRequest = {
              id: payload.new.id,
              userId: payload.new.buyer_user_id,
              listingId: payload.new.listing_id,
              createdAt: new Date(payload.new.created_at).getTime(),
              status: payload.new.status,
            }
            await handlePurchaseRequest(request)
          }
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            console.error('[Marketplace] Subscription error')
          }
        })

      channelRef.current = channel

      // Fetch any existing pending requests on mount
      try {
        const response = await fetchWithAuth(
          `${API_URL}/api/marketplace/purchase-requests/pending`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        )
        const data = await response.json()
        if (data.requests && Array.isArray(data.requests)) {
          for (const request of data.requests) {
            await handlePurchaseRequest(request)
          }
        }
      } catch (error) {
        console.error('[Marketplace] Failed to fetch pending requests:', error)
      }
    }

    // Initialize the purchase listener
    initPurchaseListener()

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [])

  return null // This is a background component with no UI
}

/**
 * Execute a purchase request
 */
async function executePurchase(request: PurchaseRequest, accessToken: string) {
  try {
    // Step 1: Load wallet (triggers passkey auth if not cached)
    let wallet
    try {
      wallet = await loadWallet()
    } catch (error) {
      await reportPurchaseFailure(
        request.id,
        `Failed to load wallet: ${error instanceof Error ? error.message : 'Unknown error'}`,
        accessToken
      )
      return
    }

    // Step 2: Execute x402 payment
    try {
      const result = await payForListing(
        request.listingId,
        wallet.privateKey,
        accessToken
      )

      // Step 3: Report success to backend (item is auto-imported by backend during purchase)
      await reportPurchaseSuccess(request.id, result, accessToken)
    } catch (paymentError) {
      // Extract detailed error message
      let errorMessage = 'Payment failed: Unknown error'

      if (paymentError && typeof paymentError === 'object') {
        const error = paymentError as any

        // Check for axios error response
        if (error.response?.data) {
          const data = error.response.data
          errorMessage = data.error || data.message || JSON.stringify(data)
        } else if (error.message) {
          errorMessage = error.message
        }

        // Add context for common errors
        if (errorMessage.toLowerCase().includes('insufficient')) {
          errorMessage = `Payment failed: Insufficient funds in wallet to complete purchase`
        } else if (errorMessage.toLowerCase().includes('timeout')) {
          errorMessage = `Payment failed: Request timed out - payment provider may be unavailable`
        } else if (errorMessage.toLowerCase().includes('network')) {
          errorMessage = `Payment failed: Network error - please check your connection`
        } else if (!errorMessage.startsWith('Payment failed:')) {
          errorMessage = `Payment failed: ${errorMessage}`
        }
      }

      await reportPurchaseFailure(request.id, errorMessage, accessToken)
    }
  } catch (error) {
    console.error(
      '[Marketplace] ✗ Unexpected error during purchase execution:',
      error
    )
    await reportPurchaseFailure(
      request.id,
      `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      accessToken
    )
  }
}

/**
 * Report successful purchase to backend
 */
async function reportPurchaseSuccess(
  requestId: string,
  result: any,
  accessToken: string
) {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/api/marketplace/purchase-requests/${requestId}/complete`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          success: true,
          purchaseId: result.purchase_id,
          itemType: result.item_type,
          shareId: result.share_id,
          txHash: result.tx_hash,
        }),
      }
    )
  } catch (error) {
    console.error('[Marketplace] Failed to report success:', error)
    throw error // Re-throw so caller knows it failed
  }
}

/**
 * Report failed purchase to backend
 */
async function reportPurchaseFailure(
  requestId: string,
  error: string,
  accessToken: string
) {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/api/marketplace/purchase-requests/${requestId}/fail`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ error }),
      }
    )

    const data = await response.json()

    // Check if purchase request was successfully recorded
    if (!response.ok) {
      console.error('[Marketplace] Failed to report purchase failure:', data)
    }
  } catch (reportError) {
    console.error('[Marketplace] ✗ Failed to report failure:', reportError)
    console.error('[Marketplace] Original error:', error)
  }
}
